import { ShieldAlert, ShieldCheck, FlaskConical, Bug, Wrench } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { Block, Tag, List } from "@/components/analysis/resultUi";
import { Fields } from "@/components/analysis/ResultPlan";

const SUBST_LABELS = { why: "Perché è pertinente", target: "Contro", timing: "Fase/momento", ipm_role: "Ruolo nella gestione integrata", considerations: "Considerazioni agronomiche", limitations: "Limitazioni e precauzioni" };

const Sub = ({ icon: Icon, children }) => (
  <p className="mb-2 mt-5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5b6b60]"><Icon size={12} />{children}</p>
);

function Substance({ s, i }) {
  return (
    <div className="rounded-xl border border-[#ddc69c]/70 p-3" data-testid={`substance-${i}`}>
      <Tag cls="bg-[#0D2517] text-[#F7F4EE]">Sostanza attiva · strategia di controllo</Tag>
      <p className="mt-2 text-sm font-semibold text-[#0D2517]">{s.name} <span className="text-xs font-normal text-[#8a958d]">({s.iso})</span></p>
      {Object.entries(SUBST_LABELS).filter(([k]) => s[k]).map(([k, l]) => (
        <p key={k} className="mt-1 text-sm text-[#2D3A30]"><span className="font-semibold text-[#5b6b60]">{l}:</span> {s[k]}</p>
      ))}
    </div>
  );
}

function VerifiedUse({ u }) {
  return (
    <div className="mt-3 rounded-xl border border-[#ddc69c]/70 p-3" data-testid={`verified-use-${u.id}`}>
      <div className="flex flex-wrap gap-1.5">
        <Tag cls="bg-[#B91C1C] text-white">Impiego autorizzato verificato su fonte ufficiale</Tag>
        {u.organic_allowed && <Tag cls="bg-[#059669] text-white">Ammesso in agricoltura biologica</Tag>}
      </div>
      <p className="mt-2 text-sm"><span className="text-[#5b6b60]">Coltura:</span> <strong>{u.crop}</strong></p>
      <p className="text-sm"><span className="text-[#5b6b60]">Avversità:</span> <strong>{u.adversity_name}</strong></p>
      <p className="text-sm"><span className="text-[#5b6b60]">{u.means_type === "sostanza_attiva" ? "Sostanza attiva" : "Mezzo tecnico"}:</span> <strong>{u.active_substance}</strong></p>
      {u.limitations && <p className="text-xs text-[#5b6b60]">Limitazioni: {u.limitations}</p>}
      {u.organic_allowed && u.organic_reference && <p className="text-xs text-[#5b6b60]">Riferimento bio: {u.organic_reference}</p>}
      <p className="mt-1 text-[11px] text-[#8a958d]">Fonte: <a className="underline" href={u.source_url} target="_blank" rel="noreferrer">{u.source_name}</a> · verificato il {fmtDate(u.verified_at)}</p>
      <p className="text-[11px] font-semibold text-[#5b6b60]">Dosi e modalità: esclusivamente da etichetta autorizzata.</p>
    </div>
  );
}

const TREATMENT_NOTE = "Utilizzare esclusivamente prodotti autorizzati per la coltura e l'avversità indicate, nel rispetto dell'etichetta. Verificare se per l'acquisto e l'impiego è richiesta specifica abilitazione.";

function TreatmentType({ names }) {
  return (
    <div data-testid="phyto-treatment-type">
      <Sub icon={FlaskConical}>C · Tipo di trattamento</Sub>
      <ul className="space-y-1">{names.map((n, i) => <li key={n} className="text-sm font-semibold text-[#0D2517]" data-testid={`treatment-type-${i}`}>{n}</li>)}</ul>
      <p className="mt-2 text-xs text-[#5b6b60]" data-testid="phyto-treatment-note">{TREATMENT_NOTE}</p>
    </div>
  );
}

export function Phyto({ phyto }) {
  const p = phyto || {};
  const names = [...new Set([...(p.substances || []).map((s) => s.name || s.iso), ...(p.verified_uses || []).map((u) => u.active_substance)].filter(Boolean))];
  return (
    <Block icon={ShieldAlert} title="Mezzi tecnici e fitosanitari" testid="result-phyto">
      {p.statement && <p className={`text-sm ${p.treatment_needed ? "text-[#2D3A30]" : "font-semibold text-[#15803D]"}`} data-testid="phyto-statement">{p.statement}</p>}
      {p.technical_means?.length > 0 && (
        <><Sub icon={Wrench}>A · Mezzi tecnici non fitosanitari</Sub>
          <div className="space-y-2">{p.technical_means.map((t, i) => <Fields key={i} item={t} testid={`technical-mean-${i}`} title={<p className="text-sm font-semibold text-[#0D2517]">{t.what}</p>} />)}</div></>
      )}
      {p.biological_means?.length > 0 && (
        <><Sub icon={Bug}>B · Mezzi biologici</Sub>
          <List items={p.biological_means.map((b) => `${b.organism}${b.target ? ` – contro ${b.target}` : ""} (dettagli al punto 4 della Gestione integrata)`)} /></>
      )}
      {names.length > 0 && <TreatmentType names={names} />}
      {p.substances?.length > 0 && <div className="mt-3 space-y-2">{p.substances.map((s, i) => <Substance key={s.iso} s={s} i={i} />)}</div>}
      {p.verified_uses?.map((u) => <VerifiedUse key={u.id} u={u} />)}
    </Block>
  );
}

export function Safety({ safety, phyto }) {
  const notice = phyto?.label_notice;
  if (!safety?.length && !notice) return null;
  return (
    <Block icon={ShieldCheck} title="Sicurezza e controllo successivo" testid="result-safety">
      {safety?.length > 0 && <List items={safety} />}
      {notice && <p className="mt-3 rounded-lg bg-[#f8f3e9] p-3 text-xs text-[#2D3A30]" data-testid="safety-label-notice">{notice}</p>}
    </Block>
  );
}
